// Medical Scores Database - Complete Collection Part 2
// Scores D-G from medicalcul.free.fr

import {
  Brain,
  Heart,
  Wind,
  Activity,
  Baby,
  Stethoscope,
  Thermometer,
  Droplets,
  Flame,
  Syringe,
  Pill,
  Bone,
  Scale,
  Clock,
  AlertTriangle,
  Calculator,
  Beaker,
  User,
  Users,
  Zap,
  Target,
  Shield
} from 'lucide-react';

export const SCORES_PART_2 = {
  // DN4 - Douleur neuropathique
  dn4: {
    name: 'Score DN4',
    category: 'Neurologie',
    icon: Brain,
    description: 'Dépistage douleur neuropathique',
    color: 'purple',
    fields: [
      { id: 'burning', label: 'Brûlure', type: 'checkbox', points: 1 },
      { id: 'cold', label: 'Froid douloureux', type: 'checkbox', points: 1 },
      { id: 'electric', label: 'Décharges électriques', type: 'checkbox', points: 1 },
      { id: 'tingling', label: 'Fourmillements', type: 'checkbox', points: 1 },
      { id: 'pins', label: 'Picotements', type: 'checkbox', points: 1 },
      { id: 'numbness', label: 'Engourdissement', type: 'checkbox', points: 1 },
      { id: 'itching', label: 'Démangeaisons', type: 'checkbox', points: 1 },
      { id: 'hypoesthesia', label: 'Hypoesthésie au tact', type: 'checkbox', points: 1 },
      { id: 'hypoPinprick', label: 'Hypoesthésie à la piqûre', type: 'checkbox', points: 1 },
      { id: 'brushAllodynia', label: 'Allodynie au frottement', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score < 4) return { level: 'success', text: `DN4 ${score}/10`, detail: 'Douleur neuropathique peu probable' };
      return { level: 'danger', text: `DN4 ${score}/10`, detail: 'Douleur neuropathique probable (Se 83%, Sp 90%)' };
    },
    formula: 'DN4 ≥ 4 = douleur neuropathique probable'
  },

  // Doloplus - Douleur personne âgée
  doloplus: {
    name: 'Échelle Doloplus-2',
    category: 'Gériatrie',
    icon: User,
    description: 'Évaluation comportementale de la douleur',
    color: 'amber',
    fields: [
      { id: 'verbal', label: 'Plaintes somatiques', type: 'select', options: [
        { value: 0, label: '0 - Pas de plainte' },
        { value: 1, label: '1 - Plaintes à la sollicitation' },
        { value: 2, label: '2 - Plaintes spontanées occasionnelles' },
        { value: 3, label: '3 - Plaintes spontanées continues' }
      ]},
      { id: 'protective', label: 'Positions antalgiques au repos', type: 'select', options: [
        { value: 0, label: '0 - Pas de position antalgique' },
        { value: 1, label: '1 - Évite certaines positions occasionnellement' },
        { value: 2, label: '2 - Position antalgique permanente' },
        { value: 3, label: '3 - Position antalgique permanente et inefficace' }
      ]},
      { id: 'protectMovement', label: 'Protection zones douloureuses', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'facial', label: 'Mimique', type: 'select', options: [
        { value: 0, label: '0 - Mimique habituelle' },
        { value: 1, label: '1 - Mimique semblant exprimer douleur à sollicitation' },
        { value: 2, label: '2 - Mimique douloureuse spontanée' },
        { value: 3, label: '3 - Mimique douloureuse permanente' }
      ]},
      { id: 'sleep', label: 'Sommeil', type: 'select', options: [
        { value: 0, label: '0 - Sommeil habituel' },
        { value: 1, label: '1 - Difficultés d\'endormissement' },
        { value: 2, label: '2 - Réveils fréquents' },
        { value: 3, label: '3 - Insomnie avec retentissement' }
      ]},
      { id: 'toilet', label: 'Toilette/Habillage', type: 'select', options: [
        { value: 0, label: '0 - Possibilités habituelles inchangées' },
        { value: 1, label: '1 - Peu diminuées' },
        { value: 2, label: '2 - Très diminuées' },
        { value: 3, label: '3 - Impossibles' }
      ]},
      { id: 'movement', label: 'Mouvements', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'communication', label: 'Communication', type: 'select', options: [
        { value: 0, label: '0 - Inchangée' },
        { value: 1, label: '1 - Intensifiée' },
        { value: 2, label: '2 - Diminuée' },
        { value: 3, label: '3 - Absence ou refus' }
      ]},
      { id: 'social', label: 'Vie sociale', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' }, { value: 3, label: '3' }
      ]},
      { id: 'behavior', label: 'Troubles du comportement', type: 'select', options: [
        { value: 0, label: '0 - Comportement habituel' },
        { value: 1, label: '1 - Troubles réactionnels occasionnels' },
        { value: 2, label: '2 - Troubles réactionnels permanents' },
        { value: 3, label: '3 - Troubles permanents' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 30 };
    },
    interpret: (score) => {
      if (score < 5) return { level: 'success', text: `Doloplus ${score}/30`, detail: 'Pas de douleur évidente' };
      if (score < 15) return { level: 'warning', text: `Doloplus ${score}/30`, detail: 'Douleur probable' };
      return { level: 'danger', text: `Doloplus ${score}/30`, detail: 'Douleur certaine - Traitement antalgique' };
    },
    formula: 'Doloplus-2 ≥ 5 = douleur probable'
  },

  // Duke - Capacité fonctionnelle
  dukeActivityStatus: {
    name: 'Duke Activity Status Index',
    category: 'Cardiologie',
    icon: Heart,
    description: 'Capacité fonctionnelle en METs',
    color: 'rose',
    fields: [
      { id: 'selfCare', label: 'Soins personnels (s\'habiller, manger)', type: 'checkbox', points: 2.75 },
      { id: 'walkIndoors', label: 'Marcher à l\'intérieur', type: 'checkbox', points: 1.75 },
      { id: 'walk12Blocks', label: 'Marcher 100-200m sur terrain plat', type: 'checkbox', points: 2.75 },
      { id: 'climbStairs', label: 'Monter un étage', type: 'checkbox', points: 5.50 },
      { id: 'lightHousework', label: 'Travaux ménagers légers', type: 'checkbox', points: 2.70 },
      { id: 'groceries', label: 'Porter les courses', type: 'checkbox', points: 2.70 },
      { id: 'heavyHousework', label: 'Travaux ménagers lourds', type: 'checkbox', points: 3.50 },
      { id: 'yardwork', label: 'Jardinage', type: 'checkbox', points: 4.50 },
      { id: 'sexualActivity', label: 'Activité sexuelle', type: 'checkbox', points: 5.25 },
      { id: 'recreation', label: 'Sports modérés (golf, danse)', type: 'checkbox', points: 6.00 },
      { id: 'strenuousSports', label: 'Sports intenses (natation, tennis)', type: 'checkbox', points: 7.50 },
      { id: 'runShort', label: 'Course courte distance', type: 'checkbox', points: 8.00 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.selfCare) total += 2.75;
      if (values.walkIndoors) total += 1.75;
      if (values.walk12Blocks) total += 2.75;
      if (values.climbStairs) total += 5.50;
      if (values.lightHousework) total += 2.70;
      if (values.groceries) total += 2.70;
      if (values.heavyHousework) total += 3.50;
      if (values.yardwork) total += 4.50;
      if (values.sexualActivity) total += 5.25;
      if (values.recreation) total += 6.00;
      if (values.strenuousSports) total += 7.50;
      if (values.runShort) total += 8.00;
      const mets = 0.43 + (0.19 * total);
      return { score: total.toFixed(1), max: 58.2, mets: mets.toFixed(1) };
    },
    interpret: (score, calculated) => {
      const mets = parseFloat(calculated?.mets || 0);
      if (mets >= 7) return { level: 'success', text: `DASI ${score} (${mets} METs)`, detail: 'Excellente capacité fonctionnelle' };
      if (mets >= 4) return { level: 'warning', text: `DASI ${score} (${mets} METs)`, detail: 'Capacité fonctionnelle modérée' };
      return { level: 'danger', text: `DASI ${score} (${mets} METs)`, detail: 'Capacité fonctionnelle limitée (<4 METs = risque chirurgical)' };
    },
    formula: 'DASI: somme activités possibles. METs = 0.43 + 0.19×DASI'
  },

  // EASI - Dermatite atopique
  easi: {
    name: 'Score EASI',
    category: 'Dermatologie',
    icon: User,
    description: 'Sévérité dermatite atopique',
    color: 'pink',
    fields: [
      { id: 'headArea', label: 'Tête/Cou - % surface atteinte', type: 'select', options: [
        { value: 0, label: '0 - 0%' }, { value: 1, label: '1 - 1-9%' }, { value: 2, label: '2 - 10-29%' },
        { value: 3, label: '3 - 30-49%' }, { value: 4, label: '4 - 50-69%' }, { value: 5, label: '5 - 70-89%' }, { value: 6, label: '6 - 90-100%' }
      ]},
      { id: 'headSeverity', label: 'Tête/Cou - Sévérité moyenne (érythème+œdème+excoriation+lichénification)', type: 'number', min: 0, max: 12 },
      { id: 'trunkArea', label: 'Tronc - % surface atteinte', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 6, label: '6' }
      ]},
      { id: 'trunkSeverity', label: 'Tronc - Sévérité moyenne', type: 'number', min: 0, max: 12 },
      { id: 'upperArea', label: 'Membres sup - % surface atteinte', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 6, label: '6' }
      ]},
      { id: 'upperSeverity', label: 'Membres sup - Sévérité moyenne', type: 'number', min: 0, max: 12 },
      { id: 'lowerArea', label: 'Membres inf - % surface atteinte', type: 'select', options: [
        { value: 0, label: '0' }, { value: 1, label: '1' }, { value: 2, label: '2' },
        { value: 3, label: '3' }, { value: 4, label: '4' }, { value: 5, label: '5' }, { value: 6, label: '6' }
      ]},
      { id: 'lowerSeverity', label: 'Membres inf - Sévérité moyenne', type: 'number', min: 0, max: 12 }
    ],
    calculate: (values) => {
      const head = (values.headArea || 0) * (values.headSeverity || 0) * 0.1;
      const trunk = (values.trunkArea || 0) * (values.trunkSeverity || 0) * 0.3;
      const upper = (values.upperArea || 0) * (values.upperSeverity || 0) * 0.2;
      const lower = (values.lowerArea || 0) * (values.lowerSeverity || 0) * 0.4;
      const total = head + trunk + upper + lower;
      return { score: total.toFixed(1), max: 72 };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 7) return { level: 'success', text: `EASI ${score}`, detail: 'Dermatite atopique légère' };
      if (s < 21) return { level: 'warning', text: `EASI ${score}`, detail: 'Dermatite atopique modérée' };
      if (s < 50) return { level: 'danger', text: `EASI ${score}`, detail: 'Dermatite atopique sévère' };
      return { level: 'danger', text: `EASI ${score}`, detail: 'Dermatite atopique très sévère' };
    },
    formula: 'EASI = Σ(Surface × Sévérité × Coefficient régional)'
  },

  // EuroQol VAS - Qualité de vie
  euroqolVas: {
    name: 'EuroQol VAS',
    category: 'Médecine interne',
    icon: Scale,
    description: 'Auto-évaluation état de santé',
    color: 'blue',
    fields: [
      { id: 'vas', label: 'Sur une échelle de 0 (pire) à 100 (meilleur), comment évaluez-vous votre santé aujourd\'hui?', type: 'number', min: 0, max: 100 }
    ],
    calculate: (values) => {
      return { score: values.vas || 50, max: 100 };
    },
    interpret: (score) => {
      if (score >= 80) return { level: 'success', text: `VAS ${score}/100`, detail: 'Bonne qualité de vie perçue' };
      if (score >= 50) return { level: 'warning', text: `VAS ${score}/100`, detail: 'Qualité de vie moyenne' };
      return { level: 'danger', text: `VAS ${score}/100`, detail: 'Qualité de vie altérée' };
    },
    formula: 'EQ-VAS: échelle visuelle analogique 0-100'
  },

  // Fagerström - Dépendance tabagique (déjà présent mais version complète)
  fagerstromFull: {
    name: 'Test de Fagerström complet',
    category: 'Toxicologie',
    icon: Pill,
    description: 'Évaluation dépendance nicotinique',
    color: 'orange',
    fields: [
      { id: 'firstCigarette', label: 'Combien de temps après le réveil fumez-vous?', type: 'select', options: [
        { value: 3, label: '≤ 5 min (3 pts)' },
        { value: 2, label: '6-30 min (2 pts)' },
        { value: 1, label: '31-60 min (1 pt)' },
        { value: 0, label: '> 60 min (0 pt)' }
      ]},
      { id: 'difficult', label: 'Trouvez-vous difficile de ne pas fumer où c\'est interdit?', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' },
        { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'mostDifficult', label: 'Quelle cigarette trouvez-vous la plus indispensable?', type: 'select', options: [
        { value: 1, label: 'La première du matin (1 pt)' },
        { value: 0, label: 'Une autre (0 pt)' }
      ]},
      { id: 'number', label: 'Combien de cigarettes fumez-vous par jour?', type: 'select', options: [
        { value: 0, label: '≤ 10 (0 pt)' },
        { value: 1, label: '11-20 (1 pt)' },
        { value: 2, label: '21-30 (2 pts)' },
        { value: 3, label: '> 30 (3 pts)' }
      ]},
      { id: 'morning', label: 'Fumez-vous plus le matin que l\'après-midi?', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' },
        { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'ill', label: 'Fumez-vous lorsque vous êtes malade au lit?', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' },
        { value: 0, label: 'Non (0 pt)' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Fagerström ${score}/10`, detail: 'Dépendance faible ou absente' };
      if (score <= 4) return { level: 'warning', text: `Fagerström ${score}/10`, detail: 'Dépendance faible à moyenne' };
      if (score <= 6) return { level: 'warning', text: `Fagerström ${score}/10`, detail: 'Dépendance moyenne' };
      if (score <= 8) return { level: 'danger', text: `Fagerström ${score}/10`, detail: 'Dépendance forte' };
      return { level: 'danger', text: `Fagerström ${score}/10`, detail: 'Dépendance très forte - Substitution nicotinique forte dose' };
    },
    formula: 'Fagerström: 6 questions = 0-10 pts'
  },

  // FAST - Trauma échographie
  fast: {
    name: 'Protocole FAST',
    category: 'Urgences',
    icon: Stethoscope,
    description: 'Focused Assessment with Sonography for Trauma',
    color: 'orange',
    fields: [
      { id: 'pericardial', label: 'Épanchement péricardique', type: 'select', options: [
        { value: 0, label: 'Négatif' },
        { value: 1, label: 'Positif' }
      ]},
      { id: 'rightUpper', label: 'Quadrant supérieur droit (Morrison)', type: 'select', options: [
        { value: 0, label: 'Négatif' },
        { value: 1, label: 'Positif' }
      ]},
      { id: 'leftUpper', label: 'Quadrant supérieur gauche (périsplénique)', type: 'select', options: [
        { value: 0, label: 'Négatif' },
        { value: 1, label: 'Positif' }
      ]},
      { id: 'pelvic', label: 'Région pelvienne (Douglas)', type: 'select', options: [
        { value: 0, label: 'Négatif' },
        { value: 1, label: 'Positif' }
      ]}
    ],
    calculate: (values) => {
      const total = Object.values(values).reduce((a, b) => a + (b || 0), 0);
      return { score: total, max: 4 };
    },
    interpret: (score) => {
      if (score === 0) return { level: 'success', text: 'FAST négatif', detail: 'Pas d\'épanchement visible - N\'exclut pas lésion organique' };
      return { level: 'danger', text: `FAST positif (${score} zone${score > 1 ? 's' : ''})`, detail: 'Épanchement intra-péritonéal ou péricardique - Scanner/Laparotomie' };
    },
    formula: 'FAST: 4 zones échographiques (péricarde, Morrison, périsplénique, Douglas)'
  },

  // FFI - Fonction rénale KDIGO
  kdigo: {
    name: 'Stades MRC (KDIGO)',
    category: 'Néphrologie',
    icon: Droplets,
    description: 'Classification maladie rénale chronique',
    color: 'blue',
    fields: [
      { id: 'gfr', label: 'DFG estimé (mL/min/1.73m²)', type: 'number', min: 0, max: 150 },
      { id: 'albuminuria', label: 'Albuminurie (mg/g créatinine)', type: 'select', options: [
        { value: 'A1', label: 'A1 - < 30' },
        { value: 'A2', label: 'A2 - 30-300' },
        { value: 'A3', label: 'A3 - > 300' }
      ]}
    ],
    calculate: (values) => {
      const gfr = values.gfr || 90;
      let stage;
      if (gfr >= 90) stage = 1;
      else if (gfr >= 60) stage = 2;
      else if (gfr >= 45) stage = '3a';
      else if (gfr >= 30) stage = '3b';
      else if (gfr >= 15) stage = 4;
      else stage = 5;
      return { score: stage, max: 5, albuminuria: values.albuminuria || 'A1' };
    },
    interpret: (score, calculated, values) => {
      const alb = values.albuminuria || 'A1';
      const stageDesc = {
        1: 'DFG normal ou élevé',
        2: 'DFG légèrement diminué',
        '3a': 'DFG légèrement à modérément diminué',
        '3b': 'DFG modérément à sévèrement diminué',
        4: 'DFG sévèrement diminué',
        5: 'Insuffisance rénale terminale'
      };
      const risk = (score >= 4 || alb === 'A3') ? 'danger' : (score >= '3a' || alb === 'A2') ? 'warning' : 'success';
      return { level: risk, text: `Stade ${score} ${alb}`, detail: stageDesc[score] };
    },
    formula: 'KDIGO: Stade G1-5 selon DFG + A1-3 selon albuminurie'
  },

  // FLIPI - Lymphome folliculaire
  flipi: {
    name: 'Score FLIPI',
    category: 'Hématologie',
    icon: Droplets,
    description: 'Pronostic lymphome folliculaire',
    color: 'red',
    fields: [
      { id: 'age', label: 'Âge > 60 ans', type: 'checkbox', points: 1 },
      { id: 'stage', label: 'Stade Ann Arbor III-IV', type: 'checkbox', points: 1 },
      { id: 'hemoglobin', label: 'Hémoglobine < 12 g/dL', type: 'checkbox', points: 1 },
      { id: 'ldh', label: 'LDH > normale', type: 'checkbox', points: 1 },
      { id: 'nodal', label: '> 4 aires ganglionnaires atteintes', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      Object.values(values).forEach(v => { if (v) total += 1; });
      return { score: total, max: 5 };
    },
    interpret: (score) => {
      if (score <= 1) return { level: 'success', text: `FLIPI ${score}`, detail: 'Risque faible - Survie à 10 ans ~70%' };
      if (score === 2) return { level: 'warning', text: `FLIPI ${score}`, detail: 'Risque intermédiaire - Survie à 10 ans ~50%' };
      return { level: 'danger', text: `FLIPI ${score}`, detail: 'Risque élevé - Survie à 10 ans ~35%' };
    },
    formula: 'FLIPI = Âge>60 + Stade III-IV + Hb<12 + LDH↑ + >4 aires'
  },

  // FRAX - Risque fracture
  frax: {
    name: 'Score FRAX simplifié',
    category: 'Rhumatologie',
    icon: Bone,
    description: 'Évaluation risque fracturaire ostéoporotique',
    color: 'amber',
    fields: [
      { id: 'age', label: 'Âge (ans)', type: 'number', min: 40, max: 90 },
      { id: 'sex', label: 'Sexe', type: 'select', options: [
        { value: 'F', label: 'Femme' },
        { value: 'M', label: 'Homme' }
      ]},
      { id: 'weight', label: 'Poids (kg)', type: 'number', min: 30, max: 150 },
      { id: 'height', label: 'Taille (cm)', type: 'number', min: 120, max: 220 },
      { id: 'fracture', label: 'ATCD fracture ostéoporotique', type: 'checkbox', points: 1 },
      { id: 'parentFracture', label: 'ATCD fracture hanche parentale', type: 'checkbox', points: 1 },
      { id: 'smoking', label: 'Tabagisme actif', type: 'checkbox', points: 1 },
      { id: 'glucocorticoids', label: 'Corticothérapie ≥ 3 mois', type: 'checkbox', points: 1 },
      { id: 'arthritis', label: 'Polyarthrite rhumatoïde', type: 'checkbox', points: 1 },
      { id: 'osteoporosis', label: 'Ostéoporose secondaire', type: 'checkbox', points: 1 },
      { id: 'alcohol', label: 'Alcool ≥ 3 verres/jour', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      // Estimation simplifiée du risque (le vrai FRAX nécessite DMO)
      const age = values.age || 60;
      const weight = values.weight || 70;
      const height = values.height || 165;
      const bmi = weight / Math.pow(height / 100, 2);
      
      let risk = (age - 40) * 0.3; // Base risk increasing with age
      if (values.sex === 'F') risk += 3;
      if (bmi < 20) risk += 2;
      if (values.fracture) risk += 4;
      if (values.parentFracture) risk += 2;
      if (values.smoking) risk += 1;
      if (values.glucocorticoids) risk += 3;
      if (values.arthritis) risk += 1;
      if (values.osteoporosis) risk += 2;
      if (values.alcohol) risk += 1;
      
      // Cap at reasonable values
      risk = Math.min(Math.max(risk, 1), 50);
      return { score: risk.toFixed(1), max: 50, unit: '%' };
    },
    interpret: (score) => {
      const s = parseFloat(score);
      if (s < 10) return { level: 'success', text: `Risque FRAX ~${score}%`, detail: 'Risque faible - Mesures hygiéno-diététiques' };
      if (s < 20) return { level: 'warning', text: `Risque FRAX ~${score}%`, detail: 'Risque modéré - Ostéodensitométrie recommandée' };
      return { level: 'danger', text: `Risque FRAX ~${score}%`, detail: 'Risque élevé - Traitement anti-ostéoporotique à discuter' };
    },
    formula: 'FRAX: estimation simplifiée. Outil complet: www.shef.ac.uk/FRAX'
  },

  // Genève simplifié - EP
  geneveSimplified: {
    name: 'Score de Genève simplifié',
    category: 'Pneumologie',
    icon: Wind,
    description: 'Probabilité embolie pulmonaire (version simplifiée)',
    color: 'sky',
    fields: [
      { id: 'age', label: 'Âge > 65 ans', type: 'checkbox', points: 1 },
      { id: 'tvp', label: 'ATCD TVP ou EP', type: 'checkbox', points: 1 },
      { id: 'surgery', label: 'Chirurgie ou fracture ≤ 1 mois', type: 'checkbox', points: 1 },
      { id: 'cancer', label: 'Cancer actif', type: 'checkbox', points: 1 },
      { id: 'legPain', label: 'Douleur unilatérale MI', type: 'checkbox', points: 1 },
      { id: 'hemoptysis', label: 'Hémoptysie', type: 'checkbox', points: 1 },
      { id: 'hr', label: 'FC 75-94/min', type: 'checkbox', points: 1 },
      { id: 'hr2', label: 'FC ≥ 95/min', type: 'checkbox', points: 2 },
      { id: 'legPainPalpation', label: 'Douleur palpation MI + œdème', type: 'checkbox', points: 1 }
    ],
    calculate: (values) => {
      let total = 0;
      if (values.age) total += 1;
      if (values.tvp) total += 1;
      if (values.surgery) total += 1;
      if (values.cancer) total += 1;
      if (values.legPain) total += 1;
      if (values.hemoptysis) total += 1;
      if (values.hr && !values.hr2) total += 1;
      if (values.hr2) total += 2;
      if (values.legPainPalpation) total += 1;
      return { score: total, max: 10 };
    },
    interpret: (score) => {
      if (score <= 2) return { level: 'success', text: `Genève simplifié ${score}`, detail: 'Probabilité faible (8%) - D-dimères' };
      if (score <= 4) return { level: 'warning', text: `Genève simplifié ${score}`, detail: 'Probabilité intermédiaire (28%) - D-dimères puis Angioscanner' };
      return { level: 'danger', text: `Genève simplifié ${score}`, detail: 'Probabilité élevée (74%) - Angioscanner' };
    },
    formula: 'Genève simplifié: version raccourcie, tous items à 1pt sauf FC≥95 (2pts)'
  },

  // GOS - Glasgow Outcome Scale
  gos: {
    name: 'Glasgow Outcome Scale',
    category: 'Neurologie',
    icon: Brain,
    description: 'Pronostic fonctionnel après traumatisme crânien',
    color: 'purple',
    fields: [
      { id: 'score', label: 'État neurologique', type: 'select', options: [
        { value: 1, label: '1 - Décès' },
        { value: 2, label: '2 - État végétatif persistant' },
        { value: 3, label: '3 - Handicap sévère (conscient mais dépendant)' },
        { value: 4, label: '4 - Handicap modéré (indépendant mais limitations)' },
        { value: 5, label: '5 - Bonne récupération (vie normale ou quasi-normale)' }
      ]}
    ],
    calculate: (values) => {
      return { score: values.score || 5, max: 5 };
    },
    interpret: (score) => {
      const descriptions = {
        1: 'Décès',
        2: 'État végétatif - Pas de conscience apparente',
        3: 'Handicap sévère - Dépendant pour AVQ',
        4: 'Handicap modéré - Indépendant, peut travailler en milieu protégé',
        5: 'Bonne récupération - Peut avoir des déficits mineurs'
      };
      const levels = { 1: 'danger', 2: 'danger', 3: 'danger', 4: 'warning', 5: 'success' };
      return { level: levels[score], text: `GOS ${score}`, detail: descriptions[score] };
    },
    formula: 'GOS 1-5: évaluation fonctionnelle post-TC'
  },

  // GUSS - Dysphagie
  guss: {
    name: 'Score GUSS',
    category: 'Neurologie',
    icon: Brain,
    description: 'Évaluation trouble de la déglutition (dysphagie)',
    color: 'purple',
    fields: [
      { id: 'vigilance', label: 'Vigilance (15 min assis)', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' }, { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'cough', label: 'Peut tousser volontairement', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' }, { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'swallow', label: 'Peut avaler sa salive', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' }, { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'drool', label: 'Pas de bavage', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' }, { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'voiceChange', label: 'Pas de modification de la voix', type: 'select', options: [
        { value: 1, label: 'Oui (1 pt)' }, { value: 0, label: 'Non (0 pt)' }
      ]},
      { id: 'water', label: 'Test eau (3 cuillères puis verre): déglutition', type: 'select', options: [
        { value: 5, label: 'Normale (5 pts)' },
        { value: 3, label: 'Retardée (3 pts)' },
        { value: 0, label: 'Impossible (0 pt)' }
      ]},
      { id: 'waterCough', label: 'Test eau: pas de toux', type: 'select', options: [
        { value: 5, label: 'Non (5 pts)' },
        { value: 0, label: 'Oui (0 pt)' }
      ]},
      { id: 'waterVoice', label: 'Test eau: voix normale après', type: 'select', options: [
        { value: 5, label: 'Oui (5 pts)' },
        { value: 0, label: 'Non (0 pt)' }
      ]}
    ],
    calculate: (values) => {
      const preliminary = (values.vigilance || 0) + (values.cough || 0) + (values.swallow || 0) + (values.drool || 0) + (values.voiceChange || 0);
      const waterTest = (values.water || 0) + (values.waterCough || 0) + (values.waterVoice || 0);
      const total = preliminary + waterTest;
      return { score: total, max: 20, preliminary: preliminary };
    },
    interpret: (score, calculated) => {
      if (calculated?.preliminary < 5) return { level: 'danger', text: `GUSS ${score}/20`, detail: 'Évaluation préliminaire échouée - NPO strict, avis ORL' };
      if (score >= 20) return { level: 'success', text: `GUSS ${score}/20`, detail: 'Dysphagie absente ou minime - Alimentation normale' };
      if (score >= 15) return { level: 'warning', text: `GUSS ${score}/20`, detail: 'Dysphagie légère - Texture molle, pas de liquides fins' };
      if (score >= 10) return { level: 'danger', text: `GUSS ${score}/20`, detail: 'Dysphagie modérée - Texture mixée, liquides épaissis' };
      return { level: 'danger', text: `GUSS ${score}/20`, detail: 'Dysphagie sévère - NPO, sonde nasogastrique' };
    },
    formula: 'GUSS: évaluation préliminaire (5 pts) + test eau (15 pts)'
  }
};
